import { Navigate, useLocation } from "react-router-dom";
import { useAuthStatus } from "./session";
import { AuthSplash } from "./AuthSplash";
import { SignupScreen } from "./SignupScreen";
import { ForwardAfterAuth } from "./ForwardAfterAuth";
import { CHECK_EMAIL_PATH } from "./paths";
import { AUTH_ENABLED } from "./config";
import { markWelcomeIntent } from "./postAuthIntent";

/* Merge the sign-up destination intent (next=welcome) into a search string,
 * preserving any invite/redirect already there. A completed sign-up should land
 * on the first-run wizard (/welcome), so we tag the query the moment we leave
 * the sign-up screen; nextAfterAuth honors it AFTER an invite or explicit
 * redirect, so a deep link still wins.
 *
 * Also persist the intent (localStorage), so it survives email verification
 * completing in a context that re-enters the app without the query — the URL
 * param stays the fast path, storage is the fallback nextAfterAuth consults. */
function withWelcomeIntent(search: string): string {
  markWelcomeIntent();
  const params = new URLSearchParams(search);
  params.set("next", "welcome");
  return `?${params.toString()}`;
}

/* The /signup route. Renders the sign-up screen for unauthenticated users, and
 * forwards anyone already authenticated on (deep link → /welcome). When auth is
 * disabled there is no sign-up — redirect to the dashboard. */
export function SignupRoute() {
  if (!AUTH_ENABLED) return <Navigate to="/" replace />;

  return <SignupGate />;
}

function SignupGate() {
  const { status } = useAuthStatus();
  const location = useLocation();

  if (status === "loading") return <AuthSplash />;

  // A just-signed-up account is unverified — send them to the check-email
  // screen, carrying the welcome intent (and any invite/redirect) so the
  // post-verification hop lands them on the first-run wizard.
  if (status === "unverified")
    return (
      <Navigate
        to={`${CHECK_EMAIL_PATH}${withWelcomeIntent(location.search)}`}
        replace
      />
    );

  // Already authenticated (e.g. a verified account hit /signup): forward like
  // login, but default to /welcome for sign-up rather than the overview.
  if (status === "authenticated") {
    return <ForwardAfterAuth search={withWelcomeIntent(location.search)} />;
  }

  return <SignupScreen />;
}
